import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { DeliverableImportTypes } from '@mono/proto-clients';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { DecisionState, DeferredNotice, diffDeliverableFields, ProposalActionBar, ProposalEditor, useProposalDecisionActions, WorkstreamProgressLine } from './proposalShared';
import { useDeliverableImportReview } from './useReview';
const { DeliverableImportDecision, DeliverableFields } = DeliverableImportTypes;
/**
 * PROPOSAL_REVISED body: the revision at `payload.proposalId` (already in
 * `review`, since a revision starts PENDING/non-superseded) diffed against
 * the proposal it superseded. The superseded proposal is SUPERSEDED, so it's
 * excluded from the default review fetch — this issues its own
 * includeSuperseded=true query to look it up, per the proto's
 * `supersedes_proposal_id` field, without changing what the default fetch
 * returns for every other card sharing that cache entry.
 */ export function ProposalRevisedCard({ payload, review }) {
    const [editing, setEditing] = useState(false);
    const [deferred, setDeferred] = useState(false);
    const actions = useProposalDecisionActions(payload);
    const { data: supersededReview } = useDeliverableImportReview(payload.programId, payload.changeSetId, payload.workstreamKey, true);
    const proposal = review.proposals.find((p)=>p.id === payload.proposalId);
    if (!proposal) {
        return /*#__PURE__*/ _jsx("div", {
            className: "mt-2 text-sm text-gray-500",
            children: "This proposal is no longer part of the staged import."
        });
    }
    const original = supersededReview?.proposals.find((p)=>p.id === proposal.supersedesProposalId);
    const workstream = review.workstreams.find((w)=>w.key === proposal.workstreamKey);
    const isPending = proposal.decision === DeliverableImportDecision.PENDING;
    const fields = proposal.proposed ?? new DeliverableFields();
    const diffRows = original?.proposed && proposal.proposed ? diffDeliverableFields(original.proposed, fields) : [];
    return /*#__PURE__*/ _jsxs("div", {
        "data-testid": Testids.deliverableImport.proposal,
        "data-proposal-id": proposal.id,
        className: "mt-2 space-y-3 text-sm text-gray-700",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "font-medium text-gray-900",
                children: [
                    "Updated Proposal — Change ",
                    proposal.ordinal
                ]
            }),
            editing ? /*#__PURE__*/ _jsx(ProposalEditor, {
                original: fields,
                needsClarification: false,
                onCancel: ()=>setEditing(false),
                onSave: (revised, reason)=>{
                    actions.save(proposal.id, proposal.workstreamKey, revised, reason).then(()=>setEditing(false));
                }
            }) : /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    proposal.revisionReason && /*#__PURE__*/ _jsxs("div", {
                        className: "text-xs text-gray-600",
                        children: [
                            "Reason: ",
                            proposal.revisionReason
                        ]
                    }),
                    diffRows.length > 0 ? /*#__PURE__*/ _jsxs("table", {
                        className: "w-full text-xs border rounded-md",
                        children: [
                            /*#__PURE__*/ _jsx("thead", {
                                className: "bg-gray-50 text-gray-500",
                                children: /*#__PURE__*/ _jsxs("tr", {
                                    children: [
                                        /*#__PURE__*/ _jsx("th", {
                                            className: "text-left font-medium px-2 py-1",
                                            children: "Field"
                                        }),
                                        /*#__PURE__*/ _jsx("th", {
                                            className: "text-left font-medium px-2 py-1",
                                            children: "Previous"
                                        }),
                                        /*#__PURE__*/ _jsx("th", {
                                            className: "text-left font-medium px-2 py-1",
                                            children: "Revised"
                                        })
                                    ]
                                })
                            }),
                            /*#__PURE__*/ _jsx("tbody", {
                                children: diffRows.map((row)=>/*#__PURE__*/ _jsxs("tr", {
                                        className: "border-t",
                                        children: [
                                            /*#__PURE__*/ _jsx("td", {
                                                className: "px-2 py-1 font-medium text-gray-600",
                                                children: row.label
                                            }),
                                            /*#__PURE__*/ _jsx("td", {
                                                className: "px-2 py-1 text-gray-500",
                                                children: row.previous
                                            }),
                                            /*#__PURE__*/ _jsx("td", {
                                                className: "px-2 py-1 text-gray-900",
                                                children: row.revised
                                            })
                                        ]
                                    }, row.key))
                            })
                        ]
                    }) : /*#__PURE__*/ _jsx("div", {
                        className: "text-xs text-gray-500",
                        children: "Loading the previous version to diff…"
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "text-xs text-gray-500",
                        children: "All other values unchanged"
                    }),
                    isPending ? deferred ? /*#__PURE__*/ _jsx(DeferredNotice, {}) : /*#__PURE__*/ _jsx(ProposalActionBar, {
                        needsClarification: false,
                        approveLabel: "Approve Revised Change",
                        approveTestid: Testids.deliverableImport.approveRevised,
                        editLabel: "Edit Again",
                        editTestid: Testids.deliverableImport.editAgain,
                        disabled: actions.isSubmitting,
                        onApprove: ()=>actions.approve(proposal.id, proposal.workstreamKey),
                        onEdit: ()=>setEditing(true),
                        onReject: (reason)=>actions.reject(proposal.id, proposal.workstreamKey, reason),
                        onDecideLater: ()=>actions.defer(proposal.id, proposal.workstreamKey).then(()=>setDeferred(true))
                    }) : /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(DecisionState, {
                                proposal: proposal
                            }),
                            /*#__PURE__*/ _jsx(WorkstreamProgressLine, {
                                workstream: workstream
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
